import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShareButtons } from '../../components/ShareButtons';
import { Helmet } from 'react-helmet-async';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { format } from 'date-fns';

interface PostSection {
  type: 'heading' | 'paragraph' | 'list';
  level?: number;
  content?: string;
  items?: Array<{
    title: string;
    items: string[];
  }>;
}

interface BlogPost {
  title: string;
  author: string;
  date: string;
  readingTime: number;
  headerImage: {
    url: string;
    alt: string;
  };
  tags: string[];
  sections: PostSection[];
}

const PostPage: React.FC = () => {
  const { postName } = useParams<{ postName: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (postName) {
      const fetchPost = async () => {
        setIsLoading(true);
        try {
          const response = await import(`./posts/${postName}/post.json`);
          setPost(response.default);
        } catch (error) {
          console.error('Error fetching post:', error);
          navigate('/blog');
        } finally {
          setIsLoading(false);
        }
      };
      fetchPost();
    }
  }, [postName, navigate]);

  if (isLoading || !post) {
    return <LoadingSpinner />;
  }

  const renderSection = (section: PostSection, index: number) => {
    switch (section.type) {
      case 'heading':
        const HeadingTag = `h${section.level}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag 
            key={index}
            className="text-2xl font-bold text-gray-900 mt-16 mb-6 leading-tight"
          >
            {section.content}
          </HeadingTag>
        );
      
      case 'paragraph':
        return (
          <p 
            key={index}
            className="text-xl text-gray-600 leading-[1.8] mb-8 font-serif"
          >
            {section.content}
          </p>
        );
      
      case 'list':
        return (
          <div key={index} className="space-y-8 mb-8">
            {section.items?.map((item, idx) => (
              <div key={idx}>
                <h4 className="font-bold text-xl mb-4">{item.title}</h4>
                <ul className="list-disc pl-8 space-y-2">
                  {item.items.map((listItem, itemIdx) => (
                    <li 
                      key={itemIdx}
                      className="text-lg text-gray-600 leading-relaxed"
                    >
                      {listItem}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Secure Anonymous TempMail Blog</title>
        <meta name="description" content={post.sections[0]?.content || ''} />
        <meta name="keywords" content={post.tags.join(', ')} />
      </Helmet>

      <div className="max-w-[44rem] mx-auto px-4 pt-8 font-serif">
        <button 
          onClick={() => navigate('/blog')}
          className="mb-4 text-gray-600 hover:text-primary-dark transition-colors duration-200 flex items-center gap-2 font-sans"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Blog
        </button>

        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 leading-tight tracking-tight">
          {post.title}
        </h1>
      </div>

      {post.headerImage && (
        <div className="w-full">
          <div className="max-w-screen-xl mx-auto px-4 py-8">
            <div className="w-[90%] mx-auto">
              <img
                src={post.headerImage.url}
                alt={post.headerImage.alt}
                className="w-full h-auto max-h-[600px] object-cover rounded-lg shadow-lg"
                loading="lazy"
              />
              {post.headerImage.alt && (
                <p className="text-center text-gray-500 text-base mt-3 font-sans">
                  {post.headerImage.alt}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[44rem] mx-auto px-4 py-8 font-serif">
        <article className="prose prose-lg max-w-none">
          {post.sections.map((section, index) => renderSection(section, index))}
        </article>

        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-gray-500 text-base font-sans">
                {format(new Date(post.date), 'MMM dd, yyyy')} • {post.readingTime} min read
              </p>
            </div>
            <ShareButtons />
          </div>
        </div>
      </div>
    </>
  );
};

export default PostPage;